/**
 * Layout config for the generic table renderer.
 *
 * Phase 7's renderer is driven by zone metadata plus a per-game layout, so that
 * most games need no custom UI at all. This is the vocabulary that layout is
 * written in.
 *
 * Deliberately about **arrangement, not appearance**: where a zone sits, how its
 * cards are stacked, what to call it. Nothing here mentions pixels, colours or
 * fonts, because a phone and a TV want very different answers to those and the
 * game has no opinion worth hearing on either.
 *
 * It is also declarative on purpose. A layout is data, so it can be sent to a
 * client, diffed, and eventually edited without a deploy.
 *
 * ## Fields the platform reads by name
 *
 * Almost everything here is *declared* rather than inferred - `seatScore` names
 * the field holding a score, `teams` names the fields holding partnerships -
 * because a renderer that guessed would be guessing per game and would be wrong
 * for the first game that disagreed.
 *
 * A small number of view fields are the exception. These are fixed names with
 * fixed shapes, read straight out of the view by any client:
 *
 * | Field        | Shape                          | What it buys |
 * |--------------|--------------------------------|--------------|
 * | `reshuffles` | `number`                       | The "shuffling" indicator, when a game recycles its discard |
 * | `trickPlays` | `{ seat, card }[]`             | Whose card is whose in a shared trick: card flights, and a trick laid out by seat |
 * | `lastTrick`  | `{ plays, winner, n }`          | The two seconds in which a finished trick is shown before it is swept away |
 * | `targetScore`| `number`                        | "First to 500" on the game badge - what winning means |
 * | `handNumber` | `number`                        | "Hand 3" in the score panel, for a game played over many |
 * | `slots`      | `number[]`                      | Where each card of a `pyramid` zone sits, row-major from the apex |
 * | `passesLeft` | `number`                        | How many more times the stock may be turned over before the deal is dead |
 *
 * They are conventions rather than declarations because they are *shapes*, not
 * choices. A target score is a target score; there is nothing for a game to
 * decide about what to call it.
 *
 * Whose score to SHOW is a different matter and stays declared - `teams` for a
 * partnership game, `seatScore` otherwise - because that genuinely varies: in
 * Spades a chair's own total is not a thing that exists. Every trick game computes `trickPlays` the same way, from
 * `TrickTaking.fromZone` or `fromSeats`, and declaring the name in each layout
 * would be four files naming the identical string. A game that publishes the
 * shape gets the behaviour; a game that does not, silently does not, and
 * nothing else changes.
 *
 * `trickPlays` in particular is the answer to "who played this card", and it is
 * computed rather than assumed for a reason worth keeping: play `i` is usually
 * the seat `i` places clockwise of the leader, but Knockout Whist eliminates
 * players, so after a round the seats still holding cards might be 0, 2 and 5.
 * A renderer that rotated from the leader itself would be wrong there - which
 * is exactly why the seats travel with the cards instead.
 *
 * The zone those plays belong to is named `trick`, and that is part of this
 * contract too. A game that publishes `trickPlays` while calling its zone
 * something else gets every card drawn twice - once at its player's chair and
 * once again in the middle - because the renderer suppresses the middle copy by
 * that name. All four trick games call it `trick`; this is the sentence that
 * says they have to.
 *
 * If a second seat-attributed zone ever appears - a melds area, a discard where
 * who-discarded matters - that is the moment to add an optional `playedBy` to
 * `ZonePlacement`. It is additive and costs nothing to delay.
 */
/**
 * How a zone's cards are arranged.
 *
 * A list rather than a bare union, because a client has to know at RUNTIME
 * which shapes exist in order to check it can draw them all. Two hand-written
 * lists in two packages cannot be compared by the type system: a game shipping
 * a shape the renderer has never heard of type-checks on both sides and draws
 * nothing. The type is derived from the list, so there is one of them.
 */
export const SHAPES = [
    /** Overlapping, only the top readable. Draw piles, discards. */
    'stack',
    /** Spread so every card is readable. Hands. */
    'fan',
    /** Laid out side by side, none overlapping. Tricks, melds. */
    'row',
    /** A count and nothing else. Face-down piles. */
    'count',
    /**
     * A triangle, where which card rests on which is the game.
     *
     * The only shape whose arrangement is a RULE rather than presentation. A
     * trick drawn in a circle loses nothing; a pyramid drawn as a row loses the
     * whole puzzle, because a card is playable exactly when the two resting on
     * it have gone.
     *
     * Reads the `slots` field - one number per card in the zone, giving its
     * position in the triangle counted row-major from the apex. The zone closes
     * up when a card leaves it, so the positions have to travel separately.
     */
    'pyramid',
    /**
     * A tableau pile: cards overlapping downward, the last one in play.
     *
     * Unlike `pyramid` this carries no rule - it is a stack you can see into,
     * and seeing what is buried under the playable card is the whole skill of a
     * game like Golf. `stack` would hide exactly that.
     */
    'column',
    /**
     * One face-down card per card held, fanned.
     *
     * For showing somebody else's hand at the table. A count says "four" and a
     * fan of four backs says the same thing the way a real table does - you read
     * how close somebody is to going out by looking at them, not at a number.
     */
    'backs',
];
/**
 * The end of a motion that is "whoever just acted" rather than a fixed zone.
 *
 * A string rather than a flag so a motion reads as a pair of places.
 */
export const SEAT_END = 'seat';
/**
 * The end of a motion that is the pot in the middle of the table.
 *
 * A motion with this at either end moves MONEY rather than a card, and that is
 * how the renderer tells the two apart - there is no separate flag, because a
 * flight to the pot is a bet and a flight out of it is a payout, and neither
 * is ever a card. Only legal when the layout declares `chips`.
 */
export const POT_END = 'pot';
/**
 * Check a layout only mentions zones the game actually declares.
 *
 * A layout naming a zone that does not exist renders an empty box and nobody
 * notices until a player asks what it is for.
 */
export function validateLayout(layout, zoneNames) {
    const problems = [];
    const known = new Set(zoneNames);
    for (const placement of [...layout.table, ...layout.hand]) {
        if (!known.has(placement.zone)) {
            problems.push(`layout references unknown zone "${placement.zone}"`);
        }
        /*
         * A pile drawn under another one has to exist too, and a placement that
         * says where it goes has to say somewhere real. Both are new enough to be
         * typed and still easy to misspell, and a wrong one here is silent: the
         * slivers simply do not appear, or the cell lands off the board.
         */
        if (placement.under !== undefined && !known.has(placement.under)) {
            problems.push(`placement for "${placement.zone}" sits under unknown zone "${placement.under}"`);
        }
        if (placement.row !== undefined && placement.row < 1) {
            problems.push(`placement for "${placement.zone}" has row ${String(placement.row)}, which is not a cell`);
        }
        if (placement.col !== undefined && placement.col < 1) {
            problems.push(`placement for "${placement.zone}" has col ${String(placement.col)}, which is not a cell`);
        }
        if (placement.col !== undefined && placement.row === undefined) {
            problems.push(`placement for "${placement.zone}" has a column but no row`);
        }
    }
    for (const badge of layout.seatBadges ?? []) {
        if (!known.has(badge))
            problems.push(`seat badge references unknown zone "${badge}"`);
    }
    /*
     * Both ends of a motion, except the seat token.
     *
     * A misnamed zone here is worse than a misnamed badge: the badge renders an
     * empty box somebody eventually asks about, while a motion to nowhere simply
     * never plays and looks like the animation was never built.
     */
    /*
     * A seat action's grace window watches a real per-seat zone. Naming one that
     * does not exist would hold every button forever, silently.
     */
    for (const action of layout.seatActions ?? []) {
        const grace = action.grace;
        if (grace && !known.has(grace.zone)) {
            problems.push(`seat action "${action.move}" watches unknown zone "${grace.zone}"`);
        }
    }
    for (const motion of layout.motions ?? []) {
        for (const end of [motion.from, motion.to]) {
            if (end === SEAT_END)
                continue;
            /*
             * The pot is a place without being a zone: it holds money rather than
             * cards, so no `ZoneSpec` describes it. A motion may name it only if
             * the game declared where its pot comes from - otherwise the flight
             * would set off from, or land on, something that is not drawn.
             */
            if (end === POT_END) {
                if (!layout.chips) {
                    problems.push(`motion "${motion.move}" names the pot, but no chips are declared`);
                }
                continue;
            }
            if (!known.has(end)) {
                problems.push(`motion "${motion.move}" references unknown zone "${end}"`);
            }
        }
    }
    /*
     * Prompts name a MOVE, which this cannot check against anything - the layout
     * has no list of moves, and it should not, because legality is the server's
     * business and changes per turn.
     *
     * So the checks are the two that do not need one. An empty name matches no
     * move and renders nothing, which looks exactly like the bug that `prompts`
     * was built to fix. A duplicate renders the same question twice, side by
     * side, which reads as a rendering fault rather than a declaration fault.
     *
     * A prompt naming a move the game never enumerates is harmless and is not an
     * error: it simply never appears, and a game may legitimately declare one for
     * a move only some rule sets use.
     */
    /*
     * The declared suit names a zone too, and it was the one that got missed.
     *
     * Every other zone reference here is checked - placements, badges, both ends
     * of every motion - and this one is no different in kind: it says which pile
     * the wild changed the meaning of, and a misnamed pile draws the suit onto
     * nothing at all. The `field` beside it is a view field, not a zone, so it is
     * unchecked for the same reason `seatScore.field` is.
     */
    if (layout.declaredSuit && !known.has(layout.declaredSuit.zone)) {
        problems.push(`declaredSuit references unknown zone "${layout.declaredSuit.zone}"`);
    }
    /*
     * Teams name view fields, not zones, so there is no list to check them
     * against - the same limit `seatScore.field` has, and for the same reason:
     * the view is the game's own shape and this file does not know it.
     *
     * What is checkable is that the two fields are not the same one. Reading the
     * scores where the seat lists should be gives every team a score of
     * `undefined` and no members, and the standings then render an empty panel -
     * a silent nothing, which is the failure mode worth spending three lines on.
     * An empty name is the same class of typo.
     */
    if (layout.teams) {
        if (layout.teams.seats === '')
            problems.push('teams has no seats field');
        if (layout.teams.score === '')
            problems.push('teams has no score field');
        if (layout.teams.seats !== '' && layout.teams.seats === layout.teams.score) {
            problems.push(`teams reads seats and scores from the same field "${layout.teams.seats}"`);
        }
    }
    const asked = new Set();
    for (const prompt of layout.prompts ?? []) {
        if (prompt.move === '')
            problems.push('prompt has no move name');
        if (prompt.label === '')
            problems.push(`prompt "${prompt.move}" has no question`);
        if (asked.has(prompt.move))
            problems.push(`prompt "${prompt.move}" is declared twice`);
        asked.add(prompt.move);
        /*
         * An empty `args` is a mistake rather than a way of saying "none".
         *
         * Omitting the field means "draw these the way you always did", which is
         * what every existing layout relies on. Declaring it as `[]` reads like a
         * deliberate statement and does nothing at all, so it is worth naming.
         */
        if (prompt.args && prompt.args.length === 0) {
            problems.push(`prompt "${prompt.move}" declares an empty args list`);
        }
    }
    // seatScore names a view field, not a zone, so there is nothing to check it
    // against here - a game that misnames it renders no score, which is visible.
    return problems;
}
//# sourceMappingURL=layout.js.map